/**
 * Sub-hook for PO Lines workflow actions: hold, release, accept, decline, critical toggle.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { purchasingAPI, PurchaseOrderLine } from '../../../../lib/api';
import { getApiErrorMessage } from '../../../../lib/api/types/common';
import { useAuth } from '../../../../contexts/AuthContext';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { useUserPermissions } from '../../../../contexts/UserPermissionsContext';
import { usePortalMode } from '../../../../hooks/usePortalMode';
import { viewerSideOf } from '../../lineStatus';
import { NextActionResult } from './types';
import { isFullyInSupplierWarehouse, lineIsFinished } from './utils';
import { isHeld, mayReviewProposal } from '../../pendingProposal';
import { defaultHoldNextAction, type HoldNextAction } from '../../HoldNextActionChoice';
import { nextActionFor } from '../../nextAction';

interface UsePOLinesActionsParams {
  fetchLines: () => Promise<void>;
  setLines: React.Dispatch<React.SetStateAction<PurchaseOrderLine[]>>;
  bustCacheRef: React.MutableRefObject<boolean>;
}

export function usePOLinesActions({ fetchLines, setLines, bustCacheRef }: UsePOLinesActionsParams) {
  const { user } = useAuth();
  const { isFeatureEnabled } = useMenuSettings();
  const { hasPurchasingWriteAccess } = useUserPermissions();
  // CR-4647: role detection follows the ACTIVE portal mode so dual-mode users who toggle
  // to supplier view get supplier actions, not just raw company_type users.
  // Portal detection aligned with the rest of this feature: POLinesTable uses
  // usePortalMode(), so use the same source here rather than reading
  // useView().portalMode directly. usePortalMode resolves company_type OR
  // platform (either casing) and only defers to the view context for dual-mode
  // users, so single-mode suppliers are classified correctly instead of relying
  // on an exact user.platform === 'SUPPLIER' string match.
  //
  // NOTE: consistency change, not a behaviour fix. For the supplier account that
  // reported Accept/Reject on a change-declined SO line, user.platform IS
  // 'SUPPLIER', so the previous check already resolved to the supplier branch;
  // this refactor does not alter what that line renders.
  const { isSupplier: actingAsSupplier, isCustomer: actingAsCustomer } = usePortalMode();

  // Hold state
  const [selectedLineForHold, setSelectedLineForHold] = useState<PurchaseOrderLine | null>(null);
  const [putOnHoldModalOpen, setPutOnHoldModalOpen] = useState(false);
  const [releaseHoldModalOpen, setReleaseHoldModalOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  // G7: the placer's answer to "whose move is it?". Defaults to their own side,
  // which is how every hold behaved before the dialog asked.
  // A placer who is not a side of the deal (the platform operator, a logistics
  // user) has no side to hand the hold to, so no nomination is offered or sent and
  // their hold stays theirs — `actingAsSupplier ? 'supplier' : 'customer'` made an
  // operator's "With me" read as a handover to the customer (F-J, blind stage 2).
  // The acting side is known here — the portal toggle is what `actingAsSupplier`
  // reads — so it is passed rather than inferred from the profile.
  const viewerSide = viewerSideOf(
    user, actingAsSupplier ? 'supplier' : actingAsCustomer ? 'customer' : null);
  const placerSide: 'customer' | 'supplier' | undefined =
    viewerSide === 'supplier' || viewerSide === 'customer' ? viewerSide : undefined;
  const [holdNextAction, setHoldNextAction] = useState<HoldNextAction>(
    defaultHoldNextAction(placerSide || 'customer'),
  );

  // Workflow state
  const [selectedLineForWorkflow, setSelectedLineForWorkflow] = useState<PurchaseOrderLine | null>(null);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  // Hold handlers
  const handlePutOnHold = useCallback((line: PurchaseOrderLine) => {
    setSelectedLineForHold(line);
    setHoldReason('');
    setHoldNextAction(defaultHoldNextAction(placerSide || 'customer'));
    setPutOnHoldModalOpen(true);
  }, [placerSide]);

  const handleReleaseHold = useCallback((line: PurchaseOrderLine) => {
    setSelectedLineForHold(line);
    setReleaseHoldModalOpen(true);
  }, []);

  const submitPutOnHold = useCallback(async () => {
    if (!selectedLineForHold || !holdReason.trim()) {
      toast.error('Please enter a reason for putting this line on hold');
      return;
    }

    try {
      const response = await purchasingAPI.putLineOnHold(selectedLineForHold.id, {
        reason: holdReason,
        ...(placerSide ? { nextActionWith: holdNextAction } : {}),
      });

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setPutOnHoldModalOpen(false);
        setSelectedLineForHold(null);
        setHoldReason('');
        setHoldNextAction(defaultHoldNextAction(placerSide || 'customer'));
        bustCacheRef.current = true;
        fetchLines();
      }
    } catch (error: unknown) {
      console.error('Failed to put line on hold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to put line on hold'));
    }
  }, [selectedLineForHold, holdReason, holdNextAction, placerSide, fetchLines, bustCacheRef]);

  const submitReleaseHold = useCallback(async () => {
    if (!selectedLineForHold) return;

    try {
      const response = await purchasingAPI.releaseLineHold(selectedLineForHold.id);

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setReleaseHoldModalOpen(false);
        setSelectedLineForHold(null);
        bustCacheRef.current = true;
        fetchLines();
      }
    } catch (error: unknown) {
      console.error('Failed to release hold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to release hold'));
    }
  }, [selectedLineForHold, fetchLines, bustCacheRef]);

  // Toggle critical flag (CR-3404)
  const handleToggleCritical = useCallback(async (line: PurchaseOrderLine) => {
    try {
      const newCriticalState = !line.is_critical;
      const response = await purchasingAPI.setLineCritical(line.id, newCriticalState);

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setLines(prevLines =>
          prevLines.map(l =>
            l.id === line.id ? { ...l, is_critical: newCriticalState } : l
          )
        );
      }
    } catch (error: unknown) {
      console.error('Failed to toggle critical:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update critical status'));
    }
  }, [setLines]);

  // Workflow action handler
  const handleWorkflowAction = useCallback(async (data: { action: string; lineId: number; comments?: string; reason?: string; reasonCodeId?: number | null; proposedDate?: string; proposedQuantity?: number; proposedPrice?: number }) => {
    try {
      let response;

      switch (data.action) {
        case 'accept':
          response = await purchasingAPI.acceptDeliveryDate(data.lineId, {
            comments: data.comments,
          });
          break;
        case 'decline':
          response = await purchasingAPI.declineDeliveryDate(data.lineId, {
            reason: data.reason ?? '',
            reasonCodeId: data.reasonCodeId,
            comments: data.comments,
          });
          break;
        default:
          throw new Error(`Unknown action: ${data.action}`);
      }

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      setAcceptModalOpen(false);
      setDeclineModalOpen(false);
      setSelectedLineForWorkflow(null);
      bustCacheRef.current = true;
      fetchLines();
    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      toast.error(getApiErrorMessage(error, 'Workflow action failed'));
      throw error;
    }
  }, [fetchLines, bustCacheRef]);

  // Determine if current user should see Accept/Reject action buttons
  // CR-4203: Now also checks role-based site permissions
  const shouldShowActions = useCallback((line: PurchaseOrderLine): boolean => {
    if (!user || !user.platform) return false;
    // CR-4697: no actions on a closed line; CR-7418 (Aaron, 19 Sep): nor a
    // cancelled one. One predicate, shared with the hold control that used to
    // sit outside both of these.
    if (lineIsFinished(line)) return false;
    // Schedule-aware tenants: hide PO-line Accept/Decline — negotiation
    // lives on the Schedule modal only (otherwise two disconnected audit
    // trails for the same logical action).
    if (isFeatureEnabled('po_line_schedules')) return false;

    const status = line.acknowledge_status;
    const isSupplier = actingAsSupplier;
    const isCustomer = !isSupplier && actingAsCustomer;

    // Supplier-side line closure: once the supplier holds enough warehouse
    // stock to cover the full ordered qty, the line is "done" from their
    // side — no more Accept / Decline / Propose. Customer view is unchanged
    // (they still need to GRN it to close the order). Gated on call-off
    // since suppliers without it have no warehouse-allocation concept.
    if (isSupplier && isFeatureEnabled('switch_call_off_tab') && isFullyInSupplierWarehouse(line)) return false;

    // CR-4647: Site-based write access only applies to customer users.
    // Suppliers always have write access to their own PO lines.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    // EXPEDITING-PARITY PR 2: a held line is out of the negotiation entirely
    // (Aaron, 16 Sep: "a supplier should not be able to make any changes if the
    // line is on hold"). Legacy bails on row.OnHold before anything else, our
    // own bulk path has always checked it, and PR 1 made the server refuse it —
    // this predicate was the one place still rendering live buttons that 400.
    if (isHeld(line)) return false;

    // The supplier's answer to a NEW order. It shares the Accept control but it
    // is an acknowledgement, not a proposal review, so it is stated here rather
    // than folded into mayReviewProposal.
    if (isSupplier && status === 'new') return true;

    // Everything else is a proposal review, and that is the counterparty's.
    // The shared rule also carries CR-7277's phantom/no-op guard, which matters
    // most from the list because Accept there is a BULK action.
    return mayReviewProposal(line, isSupplier ? 'supplier' : isCustomer ? 'customer' : null);
  }, [user, actingAsSupplier, actingAsCustomer, hasPurchasingWriteAccess, isFeatureEnabled]);

  // Determine if decline button should be shown for a line.
  const shouldShowDecline = useCallback((line: PurchaseOrderLine): boolean => {
    if (!shouldShowActions(line)) return false;

    const isSupplierDecline = actingAsSupplier;
    // CR-5892: supplier decline of new PO lines is governed by the single
    // client-controlled "Allow Supplier Decline" flag (Admin Setup → Purchasing).
    // The former "Hide Decline Button" inverse gate was removed so there is one
    // clear setting. allow_supplier_decline is merged per-tenant by the display
    // settings endpoint, so the per-company override is reflected here.
    if (isSupplierDecline && line.acknowledge_status === 'new') {
      return isFeatureEnabled('allow_supplier_decline');
    }

    return true;
  }, [user, actingAsSupplier, shouldShowActions, isFeatureEnabled]);

  // CR-4203: Now also checks role-based site permissions
  const shouldShowProposeChange = useCallback((line: PurchaseOrderLine): boolean => {
    if (!user || !user.platform) return false;
    // CR-4697 + CR-7418: closed or cancelled, nothing applies.
    if (lineIsFinished(line)) return false;
    // EXPEDITING-PARITY PR 2: nothing is proposable on a held line — see
    // shouldShowActions above for why this predicate never checked.
    if (isHeld(line)) return false;
    // Schedule-aware tenants: hide PO-line Propose — negotiation
    // lives on the Schedule modal only.
    if (isFeatureEnabled('po_line_schedules')) return false;

    const status = line.acknowledge_status;
    const isSupplier = actingAsSupplier;
    const isCustomer = !isSupplier && actingAsCustomer;

    // Supplier-side line closure (call-off suppliers only): no Propose
    // Change once the supplier holds enough warehouse stock to cover the
    // full ordered qty.
    if (isSupplier && isFeatureEnabled('switch_call_off_tab') && isFullyInSupplierWarehouse(line)) return false;

    // CR-4647: Site-based write access only applies to customer users.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    if (isCustomer) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        // Propose Change is the only route forward on a declined line,
        // whichever side made the proposal that was declined.
        case 'change_declined':
          return true;
        default:
          return false;
      }
    }

    if (isSupplier) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        // Propose Change is the only route forward on a declined line,
        // whichever side made the proposal that was declined.
        case 'change_declined':
          return true;
        default:
          return false;
      }
    }

    return false;
  }, [user, actingAsSupplier, actingAsCustomer, hasPurchasingWriteAccess, isFeatureEnabled]);

  // Get next action for display (CR-3410). Customer review 11 Sep (item
  // 26b): the rule lives in nextAction.ts so the PO header's Lines panel
  // shows exactly what this column shows.
  const getNextAction = useCallback(
    (line: PurchaseOrderLine): NextActionResult => nextActionFor(line),
    [],
  );

  return {
    // Hold state
    selectedLineForHold,
    setSelectedLineForHold,
    putOnHoldModalOpen,
    setPutOnHoldModalOpen,
    releaseHoldModalOpen,
    setReleaseHoldModalOpen,
    holdReason,
    setHoldReason,
    holdNextAction,
    setHoldNextAction,
    placerSide,

    // Workflow state
    selectedLineForWorkflow,
    setSelectedLineForWorkflow,
    acceptModalOpen,
    setAcceptModalOpen,
    declineModalOpen,
    setDeclineModalOpen,

    // Handlers
    handlePutOnHold,
    handleReleaseHold,
    submitPutOnHold,
    submitReleaseHold,
    handleToggleCritical,
    handleWorkflowAction,
    shouldShowActions,
    shouldShowDecline,
    shouldShowProposeChange,
    getNextAction,
  };
}
